import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";
import { TIMES_FINISHED, AWARDS } from "../components/status-badge.js";
import { catalogView, applyCatalogFields } from "../utils/overrides.js";

// Movies — the fifth media type (design_documents/movies-category.md). One flat
// record per film, keyed by its IMDb id (OMDb's external id): no seasons, no
// editions, no platforms. A close clone of db/games.js minus those extras, plus
// the film metadata OMDb supplies (runtime, age rating, people, IMDb rating).

const STORE = "movies";
const MEDIA = "movie";

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export async function listMovies() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getMovieById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getMovieByImdbId(imdbId) {
	if (!imdbId) return undefined;
	const db = await getDB();
	return db.getFromIndex(STORE, "imdb_id", String(imdbId));
}

export async function addMovie({
	imdb_id,
	title,
	release_year = null,
	release_date = null,
	cover_url = null,
	summary = null,
	runtime = null,
	rated = null,
	genres = [],
	directors = [],
	writers = [],
	cast = [],
	imdb_rating = null,
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
	formats = [],
}) {
	if (!imdb_id) throw new Error("A movie needs an IMDb id");
	const existing = await getMovieByImdbId(imdb_id);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		imdb_id: String(imdb_id),
		title,
		release_year,
		release_date,
		cover_url,
		summary,
		runtime,
		rated,
		genres,
		directors,
		writers,
		cast,
		imdb_rating,
		media_type: MEDIA,
		engagement_status,
		times_finished: null,
		// Canonical column the app no longer uses (times_finished replaced it);
		// kept at 0 so the synced cloud row stays valid.
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		rating: null,
		ownership_status,
		formats,
		note: null,
		awards: [],
		// Forward-looking; no UI yet (friends are deferred).
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: MEDIA, record });
	await logItemChange(MEDIA, null, record);
	return record;
}

export async function updateMovie(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Movie ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) {
				next.date_first_completed = today;
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: MEDIA, record: next });
	await logItemChange(MEDIA, current, next);
	return next;
}

export const NOTE_MAX_LENGTH = 280;

export async function setMovieNote(id, note) {
	// Keep it a note, not a review: trim and cap. Empty string clears it.
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateMovie(id, { note: trimmed === "" ? null : trimmed });
}

export async function setMovieFormats(id, list) {
	if (!Array.isArray(list)) throw new Error("formats must be an array");
	return updateMovie(id, { formats: list });
}

export async function setMovieRating(id, rating) {
	if (
		rating !== null &&
		(!Number.isInteger(rating) || rating < 1 || rating > 5)
	) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateMovie(id, { rating });
}

export async function setMovieTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateMovie(id, { times_finished: value });
}

export async function setMovieAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	const clean = list.filter((v) => valid.has(v));
	return updateMovie(id, { awards: clean });
}

export async function deleteMovie(id) {
	const db = await getDB();
	// Read first so the cloud delete op can carry the external id (imdb_id).
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		await queueCloudWrite({ type: "delete", mediaType: MEDIA, record: current });
		await logItemChange(MEDIA, current, null);
	}
}

export async function deleteMovieByImdbId(imdbId) {
	const existing = await getMovieByImdbId(imdbId);
	if (!existing) return false;
	await deleteMovie(existing.id);
	return true;
}

// Catalog fields that only fill in when the stored record has nothing yet (a
// record that arrived without them keeps whatever OMDb says now; a value we
// already hold is left alone so a temporary OMDb blank can't wipe it).
const FILL_WHEN_MISSING = [
	"summary",
	"runtime",
	"rated",
	"imdb_rating",
	"cover_url",
];
const FILL_WHEN_EMPTY = ["genres", "directors", "writers", "cast"];

// Pure: what changes when a fresh OMDb record arrives for a tracked movie. The
// release date/year are AUTHORITATIVE from the catalog — a moved or newly
// announced date overwrites what we stored, and a date OMDb has since dropped
// clears to null ("TBD"). Everything else fills in only when missing. Returns
// null when nothing would change.
export function movieCatalogPatch(current, fresh) {
	if (!current || !fresh) return null;
	// Belt and braces: never apply another film's record.
	if (
		fresh.imdb_id != null &&
		current.imdb_id != null &&
		String(fresh.imdb_id) !== String(current.imdb_id)
	) {
		return null;
	}
	const patch = {};
	if ("release_date" in fresh) {
		const date = fresh.release_date ?? null;
		if (date !== (current.release_date ?? null)) patch.release_date = date;
	}
	if ("release_year" in fresh) {
		const year = fresh.release_year ?? null;
		if (year !== (current.release_year ?? null)) patch.release_year = year;
	}
	for (const key of FILL_WHEN_MISSING) {
		if (current[key] == null && fresh[key] != null) patch[key] = fresh[key];
	}
	for (const key of FILL_WHEN_EMPTY) {
		if ((current[key] ?? []).length === 0 && (fresh[key] ?? []).length > 0) {
			patch[key] = fresh[key];
		}
	}
	return Object.keys(patch).length > 0 ? patch : null;
}

// Apply a fresh OMDb record to a tracked movie (see movieCatalogPatch). Writes —
// and syncs — only when something actually changed, so a daily re-check of an
// unchanged film costs no cloud traffic. Resolves { record, changed }, or null if
// the movie is gone.
export async function applyCatalogRefresh(id, fresh) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) return null;
	// Diff against what the catalog last said (not the user's edits), then write
	// through the override layer: an edited field keeps the user's value and only
	// its source_values entry follows OMDb (utils/overrides.js).
	const patch = movieCatalogPatch(catalogView(current), fresh);
	if (!patch) return { record: current, changed: false };
	const { next: applied, changed } = applyCatalogFields(current, patch);
	if (!changed) return { record: current, changed: false };
	const next = { ...applied, updated_at: nowIso() };
	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: MEDIA, record: next });
	return { record: next, changed: true };
}

// "2h 58m" from a minute count — lives in utils/format.js (the list rows need it
// without pulling in the DB layer); re-exported here for the movie screens.
export { formatRuntime } from "../utils/format.js";
